import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { formatCurrency } from '@shared';
import { AccountDetailsAccordion } from './AccountDetailsAccordion';
import { AddAssetSection } from './AddAssetSection';
import { useSelector } from 'react-redux';
import { selectLtcFinancialPlanningSelectedAccountTypes } from '@client/model/selectors';

interface AdjustFundingModalProps {
  open: boolean;
  onClose: () => void;
  policyPremium: number;
  totalExistingAssetsValue: number;
  selectedPolicyNames: string[];
}

export function AdjustFundingModal({
  open,
  onClose,
  policyPremium,
  totalExistingAssetsValue,
  selectedPolicyNames,
}: AdjustFundingModalProps) {
  const shortfall = policyPremium - totalExistingAssetsValue;
  const hasSurplus = shortfall < 0;
  const selectedAccountTypes = useSelector(selectLtcFinancialPlanningSelectedAccountTypes);

  // Format policy name(s) for display
  const policyNameText =
    selectedPolicyNames.length > 0 ? selectedPolicyNames.join(', ') : 'your selected policies';

  return (
    <Dialog
      open={open}
      onOpenChange={onClose}
    >
      <DialogContent className="max-h-[90vh] max-w-4xl gap-0 p-0">
        <DialogHeader className="border-border border-b p-6 pb-4">
          <DialogTitle className="text-xl font-semibold text-gray-900">
            Adjust Your Funding Sources
          </DialogTitle>
        </DialogHeader>

        <div className="max-h-[65vh] flex-1 overflow-y-auto">
          <div className="space-y-6 p-6">
            {/* Funding Status Alert */}
            <div
              className={
                hasSurplus
                  ? 'rounded-xl border border-gray-200 bg-gray-50 p-6'
                  : 'rounded-xl border border-amber-500/30 bg-amber-500/10 p-6'
              }
            >
              <div className="flex-1">
                <div className="mb-1 flex items-center gap-2">
                  {hasSurplus && <CheckCircle2 className="h-5 w-5 text-green-600" />}
                  <h4 className="font-semibold text-gray-900">
                    {hasSurplus ? 'Funding Overview' : 'Funding Deficit'}
                  </h4>
                </div>
                <p className="mb-3 text-sm text-gray-600">
                  {hasSurplus ? (
                    <>
                      Your current assets cover the cost of{' '}
                      <span className="font-medium text-gray-900">{policyNameText}</span>.
                    </>
                  ) : (
                    <>
                      Your current assets are insufficient to cover the cost of{' '}
                      <span className="font-medium text-gray-900">{policyNameText}</span>.
                    </>
                  )}
                </p>
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div>
                    <p className="text-gray-600">Policy Cost</p>
                    <p className="font-semibold text-gray-900">{formatCurrency(policyPremium)}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Existing Assets</p>
                    <p className="font-semibold text-gray-900">
                      {formatCurrency(totalExistingAssetsValue)}
                    </p>
                  </div>
                  <div>
                    <p className={hasSurplus ? 'text-gray-600' : 'text-amber-600'}>
                      {hasSurplus ? 'Surplus' : 'Deficit'}
                    </p>
                    <p
                      className={
                        hasSurplus ? 'font-semibold text-gray-900' : 'font-semibold text-amber-600'
                      }
                    >
                      {formatCurrency(Math.abs(shortfall))}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Account Details Section */}
            <div className="space-y-3">
              <AccountDetailsAccordion
                selectedAccountTypes={selectedAccountTypes}
                showValueBadge={true}
              />
            </div>

            {/* Add Asset Section */}
            <AddAssetSection />
          </div>
        </div>

        {shortfall <= 0 && (
          <div className="border-border flex justify-end gap-3 border-t p-4">
            <Button
              onClick={onClose}
              className="rounded-full px-12 py-3 text-lg font-medium shadow-sm"
            >
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
